import type { BooleanInput } from '@angular/cdk/coercion';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideChevronDown } from '@ng-icons/lucide';
import { BrnFieldControlDescribedBy } from '@spartan-ng/brain/field';
import { BrnSelectTrigger } from '@spartan-ng/brain/select';
import { hlm } from '@spartan-ng/helm/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

const selectTriggerVariants = cva(
  'data-placeholder:text-muted-foreground data-[matches-spartan-invalid=true]:ring-destructive/20 dark:data-[matches-spartan-invalid=true]:ring-destructive/40 data-[matches-spartan-invalid=true]:border-destructive dark:data-[matches-spartan-invalid=true]:border-destructive/50 gap-1.5 px-3 py-2 text-sm transition-[color,box-shadow,background-color] data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:gap-1.5 flex items-center justify-between whitespace-nowrap outline-none disabled:cursor-not-allowed *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center [&_ng-icon]:pointer-events-none [&_ng-icon]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-input/50 focus-visible:border-ring focus-visible:ring-ring/30 rounded-3xl border border-transparent focus-visible:ring-3 data-[matches-spartan-invalid=true]:ring-3 w-fit disabled:opacity-50',
        'surface-secondary':
          'bg-[var(--corporate-select-background)]! text-[color:var(--corporate-text-primary)] text-base rounded-3xl border border-transparent hover:border-white focus:border-[color:var(--corporate-select-focus-border-color)] focus-visible:border-[color:var(--corporate-select-focus-border-color)] focus-visible:ring-2 focus-visible:ring-[var(--corporate-focus-ring)] aria-expanded:border-[color:var(--corporate-select-focus-border-color)] min-w-40 data-placeholder:text-[color:var(--corporate-text-primary)] disabled:opacity-100 disabled:bg-[var(--corporate-select-background)] disabled:text-[var(--corporate-text-primary)] *:data-[slot=select-placeholder]:text-[var(--corporate-text-primary)] *:data-[slot=select-value]:text-[var(--corporate-text-primary)] [&_ng-icon]:text-[var(--corporate-text-primary)]',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export type SelectTriggerVariants = VariantProps<typeof selectTriggerVariants>;

@Component({
  selector: 'hlm-select-trigger',
  imports: [NgIcon, BrnSelectTrigger, BrnFieldControlDescribedBy],
  providers: [provideIcons({ lucideChevronDown })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      brnSelectTrigger
      brnFieldControlDescribedBy
      [forceInvalid]="forceInvalid()"
      [id]="buttonId()"
      [class]="_computedClass()"
      [attr.data-size]="size()"
      data-slot="select-trigger"
    >
      <ng-content />
      <ng-icon name="lucideChevronDown" class="text-[length:--spacing(4)] ms-auto" />
    </button>
  `,
})
export class HlmSelectTrigger {
  private static _id = 0;

  public readonly userClass = input<ClassValue>('', { alias: 'class' });
  protected readonly _computedClass = computed(() =>
    hlm(selectTriggerVariants({ variant: this.variant() }), this.userClass())
  );

  public readonly buttonId = input<string>(`hlm-select-trigger-${HlmSelectTrigger._id++}`);

  public readonly size = input<'default' | 'sm'>('default');

  public readonly variant = input<SelectTriggerVariants['variant']>('default');

  /** Whether to force the trigger into an invalid state. */
  public readonly forceInvalid = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });
}
